import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../ComingSoon/_shared/AppIcon';
import { LeaveBalanceView, LeaveStatementView, QuotaUnit } from '../../../../models/attendance';
import { getMyStatement } from '../../../../api/Attendance/leaves';
import { appDate } from '../../../../utils/appDateFormat';
import { BalanceBreakdownLedger } from '../../../../components/leaves/BalanceBreakdownLedger';
import { BalanceWarningBadge, resolveBalanceWarnings } from '../../../../components/leaves/BalanceWarningBadge';
import { parseApiDateOnly } from '@/utils/apiDate';

interface LeaveBalancesCardsProps {
  balances: LeaveBalanceView[];
}

interface ColorClasses {
  bg: string;
  border: string;
  icon: string;
  progress: string;
  text: string;
}

const LEAVE_TYPE_COLORS: Record<string, string> = {
  'annual': 'blue',
  'sick': 'red',
  'personal': 'purple',
  'maternity': 'green',
  'paternity': 'green',
  'unpaid': 'gray',
  'compassionate': 'orange',
};

const LEAVE_TYPE_ICONS: Record<string, string> = {
  'annual': 'Calendar',
  'sick': 'Heart',
  'personal': 'User',
  'maternity': 'Baby',
  'paternity': 'Baby',
  'unpaid': 'DollarSign',
  'compassionate': 'HeartHandshake',
};

function getColorForLeaveType(leaveTypeName: string): string {
  const lower = leaveTypeName.toLowerCase();
  for (const [key, color] of Object.entries(LEAVE_TYPE_COLORS)) {
    if (lower.includes(key)) return color;
  }
  return 'blue';
}

function getIconForLeaveType(leaveTypeName: string): string {
  const lower = leaveTypeName.toLowerCase();
  for (const [key, icon] of Object.entries(LEAVE_TYPE_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return 'Calendar';
}

function getColorClasses(color: string): ColorClasses {
  const colorMap: Record<string, ColorClasses> = {
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-200', icon: 'text-blue-600', progress: 'bg-blue-600', text: 'text-blue-700' },
    red: { bg: 'bg-red-500/10', border: 'border-red-200', icon: 'text-red-600', progress: 'bg-red-600', text: 'text-red-700' },
    purple: { bg: 'bg-purple-500/10', border: 'border-purple-200', icon: 'text-purple-600', progress: 'bg-purple-600', text: 'text-purple-700' },
    green: { bg: 'bg-green-500/10', border: 'border-green-200', icon: 'text-green-600', progress: 'bg-green-600', text: 'text-green-700' },
    gray: { bg: 'bg-gray-500/10', border: 'border-border', icon: 'text-gray-600', progress: 'bg-gray-600', text: 'text-gray-700' },
    orange: { bg: 'bg-orange-500/10', border: 'border-orange-200', icon: 'text-orange-600', progress: 'bg-orange-600', text: 'text-orange-700' },
  };
  return colorMap[color] || colorMap.blue;
}

function formatAccrualFrequency(freq: string | null | undefined, t: (key: string) => string): string | null {
  if (!freq) return null;
  const map: Record<string, string> = {
    Monthly: t('accrualMonthly'),
    Quarterly: t('accrualQuarterly'),
    SemiAnnually: t('accrualSemiAnnually'),
    Annually: t('accrualAnnually'),
    OnHireDate: t('accrualOnHireDate'),
  };
  return map[freq] ?? freq;
}

const r = (n: number) => Math.round(n * 100) / 100;

const LeaveBalancesCards: React.FC<LeaveBalancesCardsProps> = ({ balances }) => {
  const { t } = useTranslation(['leaves', 'common']);
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});
  const [statements, setStatements] = useState<Record<number, LeaveStatementView | null>>({});
  const [loadingCards, setLoadingCards] = useState<Record<number, boolean>>({});
  const [statementsFetched, setStatementsFetched] = useState(false);

  const fetchStatements = useCallback(async () => {
    if (statementsFetched) return;
    setStatementsFetched(true);
    try {
      const year = new Date().getFullYear();
      const result = await getMyStatement(year);
      if (result.errors.length === 0 && result.payload) {
        const map: Record<number, LeaveStatementView> = {};
        for (const stmt of result.payload.statements) {
          map[stmt.leaveTypeId] = stmt;
        }
        setStatements(map);
      }
    } catch {
      // Statement data unavailable — ledger will show "no data"
    }
  }, [statementsFetched]);

  const handleToggle = useCallback(async (leaveTypeId: number) => {
    const wasExpanded = expandedCards[leaveTypeId];
    setExpandedCards(prev => ({ ...prev, [leaveTypeId]: !wasExpanded }));

    if (!wasExpanded && !statementsFetched) {
      setLoadingCards(prev => ({ ...prev, [leaveTypeId]: true }));
      await fetchStatements();
      setLoadingCards(prev => ({ ...prev, [leaveTypeId]: false }));
    }
  }, [expandedCards, statementsFetched, fetchStatements]);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    try {
      return appDate(parseApiDateOnly(dateStr)) || null;
    } catch {
      return null;
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {balances.map((balance) => {
        const color = getColorForLeaveType(balance.leaveType);
        const icon = getIconForLeaveType(balance.leaveType);
        const colors = getColorClasses(color);
        const used = balance.used ?? 0;
        const usagePercentage = balance.eligibility > 0 ? Math.round((used / balance.eligibility) * 100) : 0;
        const remainingPercentage = balance.eligibility > 0 ? Math.round((balance.balance / balance.eligibility) * 100) : 0;
        const unitLabel = balance.eligibilityUnit === QuotaUnit.Hours ? t('hrs') : t('days');
        const accrualLabel = formatAccrualFrequency(balance.accrualFrequency, t);

        const warnings = resolveBalanceWarnings({
          balance: balance.balance,
          warningThreshold: balance.warningThreshold,
          carryForwardBalance: balance.carryForwardBalance,
          carryForwardExpiry: balance.carryForwardExpiry,
        });

        return (
          <div
            key={balance.leaveTypeId}
            data-testid="leave-balance-card"
            className={`p-6 rounded-xl border ${colors.border} ${colors.bg} hover:shadow-lg transition-all duration-200`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className={`w-10 h-10 rounded-lg ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                  <Icon name={icon} size={20} className={colors.icon} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{balance.leaveType}</h3>
                  <p className="text-sm text-muted-foreground">{t('Leave Balance')}</p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${colors.text} ${colors.bg}`}>
                {r(balance.balance)} {t('days left')}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{t('Usage')}</span>
                <span className="text-sm font-medium text-foreground">
                  {r(used)} / {r(balance.eligibility)} {unitLabel}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${colors.progress} transition-all duration-300`}
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-muted-foreground">
                  {usagePercentage}% {t('used')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {remainingPercentage}% {t('remaining')}
                </span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">{r(balance.eligibility)}</div>
                <div className="text-xs text-muted-foreground">{t('Entitlement')}</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${colors.text}`}>{r(used)}</div>
                <div className="text-xs text-muted-foreground">{t('Days Used')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">{r(balance.balance)}</div>
                <div className="text-xs text-muted-foreground">{t('Available')}</div>
              </div>
            </div>

            {/* Accrual & Renewal Info */}
            {(accrualLabel || balance.renewalDate) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4 text-xs text-muted-foreground">
                {accrualLabel && (
                  <span>
                    {t('Accrual Rate')}: <span className="font-medium text-foreground">{balance.accrualRate != null ? r(balance.accrualRate) : '—'} {unitLabel}/{accrualLabel}</span>
                  </span>
                )}
                {balance.renewalDate && (
                  <span>
                    {t('Renewal Date')}: <span className="font-medium text-foreground">{formatDate(balance.renewalDate)}</span>
                  </span>
                )}
              </div>
            )}

            {/* Balance Breakdown Ledger */}
            <BalanceBreakdownLedger
              variant="card"
              statement={statements[balance.leaveTypeId] ?? null}
              isLoading={loadingCards[balance.leaveTypeId] ?? false}
              expanded={expandedCards[balance.leaveTypeId] ?? false}
              onToggle={() => handleToggle(balance.leaveTypeId)}
            />

            {/* Balance warnings (low, depleted, carry-forward expiry) */}
            {warnings.map((status) => (
              <BalanceWarningBadge
                key={status}
                status={status}
                balance={balance.balance}
                carryForwardDays={balance.carryForwardBalance}
                expiryDate={balance.carryForwardExpiry}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default LeaveBalancesCards;
